import { useCallback, useEffect, useState } from 'react';
import { makeStyles } from 'tss-react/mui';
import { useFormik } from 'formik';
import { object, string } from 'yup';
import { format } from 'date-fns';
import { Grid } from '@mui/material';
import { FormModal } from './FormModal';
import { AutocompleteInput, FileInput, SelectInput } from '../inputs';
import {
  useDispatch,
  useSelector,
  carriersSelectors,
  getCarrierList,
  getCarrierAccountsList,
} from '../../../state';
import { useNotify } from '../../../hooks';
import { authPost } from '../../../lib';
import { FileType, IdNamePair } from '../../../types';

interface Props {
  open: boolean;
  handleClose: () => any;
}

interface State {
  carrierCode: string;
  carrierAccountID: string;
}

const initialState: State = {
  carrierCode: '',
  carrierAccountID: '',
};

export const BillImportForm = ({ open, handleClose }: Props) => {
  const { classes, cx } = useStyles();
  const dispatch = useDispatch();
  const notify = useNotify();

  const [submitting, setSubmitting] = useState(false);
  const [files, setFiles] = useState<Array<FileType>>([]);
  const [accounts, setAccounts] = useState<Array<IdNamePair>>([]);

  const carriers = useSelector(carriersSelectors.basicList);

  useEffect(() => {
    dispatch(getCarrierList());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = useCallback(
    async (values: State) => {
      if (!files.length) {
        return notify('Please upload a file', 'error');
      }
      setSubmitting(true);

      const formData = new FormData();
      formData.append('carrierCode', values.carrierCode);
      formData.append('carrierAccountID', values.carrierAccountID);
      formData.append('File', files[0].rawFile);

      const { error } = await authPost(
        '/accounting/bills/file-import',
        formData,
        undefined,
        undefined,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        },
      );
      setSubmitting(false);
      if (error) {
        notify(
          error.message ||
            error.Message ||
            error.title ||
            'Something went wrong',
          'error',
        );
      } else {
        notify(
          'Bill import is in progress, please refresh your page to see the results.',
          'success',
        );
        handleClose();
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [files],
  );

  const formik = useFormik({
    initialValues: initialState,
    enableReinitialize: false,
    onSubmit: handleSubmit,
    validationSchema: schema,
  });

  const handleCarrierChange = useCallback(
    async (e: React.ChangeEvent<any>) => {
      const code = e.target.value;
      formik.setFieldValue('carrierAccountID', '');
      setAccounts([]);

      if (code) {
        const carrier = carriers.find(
          (c: IdNamePair) => String(c.id) === String(code),
        );
        if (carrier) {
          const { data } = await dispatch(
            getCarrierAccountsList({
              code: carrier.code ?? String(carrier.id),
            }),
          );
          if (data) {
            setAccounts(data);
          }
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [carriers],
  );

  return (
    <FormModal
      open={open}
      handleClose={handleClose}
      title={<span className={classes.title}>Import bills</span>}
      callback={formik.handleSubmit}
      btnText='SAVE'
      typeSubmit
      maxWidth='lg'
      paperProps={{ style: { width: 720 } }}
      submitting={submitting}
    >
      <div className={classes.layout}>
        <Grid container spacing={5} rowSpacing={2}>
          <Grid
            style={{ paddingBottom: 0 }}
            size={{
              sm: 6,
            }}
          >
            <AutocompleteInput
              formikProps={formik}
              textFieldProps={{
                name: 'carrierCode',
                label: 'Carrier',
                autoComplete: 'off',
                required: true,
                className: classes.input,
                onChange: handleCarrierChange,
              }}
              autocompleteProps={{
                options: carriers,
              }}
            />
          </Grid>
          <Grid
            style={{ paddingBottom: 0 }}
            size={{
              sm: 6,
            }}
          >
            <SelectInput
              name='carrierAccountID'
              formikProps={formik}
              label='Account'
              className={classes.input}
              options={accounts}
              required
            />
          </Grid>
        </Grid>
        <div className={classes.fileInput}>
          <FileInput
            files={files}
            setFiles={setFiles}
            filePrefix={`bills/upload-${format(new Date(), 'Pp')}`}
            multiple={false}
            acceptFileTypes={{ 'text/*': ['.txt', '.csv'] }}
            uploadText={
              <div className={classes.inputTextContainer}>
                <span className={classes.inputText}>
                  Drag 'n' drop a file here, or click to select files
                </span>
                <span className={cx(classes.inputText, classes.inputSmallText)}>
                  Files should be in csv/txt format
                </span>
              </div>
            }
          />
        </div>
      </div>
    </FormModal>
  );
};

const schema = object().shape({
  carrierCode: string().nullable().required('Carrier is required'),
  carrierAccountID: string().required('Account is required'),
});

const useStyles = makeStyles({ name: { BillImportForm } })((theme) => ({
  layout: {
    padding: '0 16px 16px 16px',
  },
  title: {
    fontFamily: 'Montserrat',
    fontSize: 20,
    fontWeight: 600,
  },
  input: {
    width: '100%',
  },
  fileInput: {
    marginTop: 70,
  },
  inputTextContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  inputText: {
    color: '#A5AABD',
    fontSize: 16,
    letterSpacing: 0,
  },
  inputSmallText: {
    fontSize: 12,
  },
}));
